import React, { useState, useEffect, memo } from "react";
import { FaBirthdayCake, FaCheck, FaTimes } from "react-icons/fa";
import { IconWrapper } from "./IconWrapper";

const CURRENT_YEAR = new Date().getFullYear();
const MAX_AGE = 120;
const MIN_YEAR = CURRENT_YEAR - MAX_AGE;

const YEARS = Array.from(
  { length: CURRENT_YEAR - MIN_YEAR + 1 },
  (_, i) => CURRENT_YEAR - i
);

const MONTHS = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const AgeVerification: React.FC = memo(() => {
  const [show, setShow] = useState(false);
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const ageVerified = localStorage.getItem("ageVerified");
    if (!ageVerified) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  if (!show) return null;

  const validateAndGetAge = (
    dayStr: string,
    monthStr: string,
    yearStr: string
  ): number | null => {
    const d = parseInt(dayStr, 10);
    const m = parseInt(monthStr, 10);
    const y = parseInt(yearStr, 10);

    if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) {
      return null;
    }

    const birthDate = new Date(y, m - 1, d);
    const today = new Date();

    if (
      birthDate.getFullYear() !== y ||
      birthDate.getMonth() !== m - 1 ||
      birthDate.getDate() !== d
    ) {
      return null;
    }

    if (birthDate > today) {
      return null;
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const handleVerify = () => {
    setError("");

    if (!day || !month || !year) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    const age = validateAndGetAge(day, month, year);

    if (age === null) {
      setError("Date de naissance invalide");
      return;
    }

    if (age < 18) {
      setError("Vous devez avoir au moins 18 ans pour accéder à ce site");
      return;
    }

    if (age > MAX_AGE) {
      setError("Date de naissance invalide");
      return;
    }

    localStorage.setItem("ageVerified", "true");
    setShow(false);
  };

  const handleReject = () => {
    window.location.href = "https://www.joueurs-info-service.fr";
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-75 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-scale-in">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IconWrapper
              icon={FaBirthdayCake}
              className="text-primary-600"
              size={40}
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Vérification de l&apos;âge
          </h2>
          <p className="text-gray-600">
            Vous devez avoir au moins 18 ans pour accéder à ce site
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Date de naissance
          </label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Jour</option>
                {DAYS.map((d) => (
                  <option key={d} value={d.toString().padStart(2, "0")}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Mois</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Année</option>
                {YEARS.map((y) => (
                  <option key={y} value={y.toString()}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <p className="text-red-600 text-sm mt-2 flex items-center">
              <IconWrapper icon={FaTimes} className="mr-1" size={14} />
              {error}
            </p>
          )}
        </div>

        <div className="bg-primary-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700 text-center">
            <strong>Jeu responsable :</strong> Les paris sportifs peuvent créer
            une dépendance. Jouez avec modération. En cas de problème, contactez{" "}
            <a
              href="https://www.joueurs-info-service.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Joueurs-info-service.fr
            </a>
          </p>
        </div>

        <div className="flex flex-col space-y-3">
          <button
            onClick={handleVerify}
            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
          >
            <IconWrapper icon={FaCheck} size={18} />
            <span>J&apos;ai plus de 18 ans</span>
          </button>
          <button
            onClick={handleReject}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            J&apos;ai moins de 18 ans
          </button>
        </div>
      </div>
    </div>
  );
});

AgeVerification.displayName = "AgeVerification";

export default AgeVerification;
